import { useState } from 'react'
import { Phone, MessageCircle, Clock, Link as LinkIcon, Search, Filter, Download } from 'lucide-react'

export default function CallLogsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('all') // all, call, text
  const [filterDirection, setFilterDirection] = useState('all') // all, inbound, outbound
  const [selectedLog, setSelectedLog] = useState(null)

  // Mock call logs data
  const callLogs = [
    {
      id: 1,
      phoneNumber: '+1-412-555-0123',
      callType: 'call',
      direction: 'inbound',
      timestamp: '2024-04-18 14:32:15',
      duration: 342,
      durationFormatted: '5m 42s',
      message: null,
      transcript: 'Customer called about billing issue with their subscription...',
      status: 'completed',
      sentiment: 'frustrated',
      intent: 'support',
      linkedTicket: '#TKT-1042',
      ticketId: 1042,
      aiSummary: 'Customer disputes charge on account. Requires refund review.',
      preview: '5m 42s call - billing issue'
    },
    {
      id: 2,
      phoneNumber: '+1-412-555-0124',
      callType: 'text',
      direction: 'inbound',
      timestamp: '2024-04-18 13:15:22',
      duration: null,
      message: 'Hi, I need help with my password reset. I\'m locked out of my account.',
      status: 'completed',
      sentiment: 'neutral',
      intent: 'support',
      linkedTicket: '#TKT-1041',
      ticketId: 1041,
      aiSummary: 'User account lockout - password reset needed',
      preview: 'Hi, I need help with my password reset...'
    },
    {
      id: 3,
      phoneNumber: '+1-412-555-0125',
      callType: 'call',
      direction: 'inbound',
      timestamp: '2024-04-18 11:45:00',
      duration: 156,
      durationFormatted: '2m 36s',
      message: null,
      transcript: 'Quick inquiry about features in the Pro plan...',
      status: 'completed',
      sentiment: 'positive',
      intent: 'sales',
      linkedTicket: null,
      ticketId: null,
      aiSummary: 'Sales inquiry - Pro plan features. High conversion potential.',
      preview: '2m 36s call - Pro plan inquiry'
    },
    {
      id: 4,
      phoneNumber: '+1-412-555-0126',
      callType: 'text',
      direction: 'outbound',
      timestamp: '2024-04-18 09:30:45',
      duration: null,
      message: 'Hi! Following up on your support request. We\'ve resolved the issue on our end.',
      status: 'completed',
      sentiment: 'positive',
      intent: 'support',
      linkedTicket: '#TKT-1040',
      ticketId: 1040,
      aiSummary: 'Outbound follow-up confirming resolution',
      preview: 'Hi! Following up on your support request...'
    },
    {
      id: 5,
      phoneNumber: '+1-412-555-0127',
      callType: 'call',
      direction: 'inbound',
      timestamp: '2024-04-18 08:22:10',
      duration: null,
      durationFormatted: 'Voicemail',
      message: null,
      transcript: 'Customer left voicemail regarding product defect...',
      status: 'voicemail',
      sentiment: 'negative',
      intent: 'complaint',
      linkedTicket: null,
      ticketId: null,
      aiSummary: 'Product defect complaint - customer requesting callback',
      preview: 'Voicemail - product defect complaint'
    },
  ]

  const filteredLogs = callLogs.filter(log => {
    const matchesSearch = log.phoneNumber.includes(searchQuery) ||
      log.intent.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.linkedTicket?.includes(searchQuery) || false)
    const matchesType = filterType === 'all' || log.callType === filterType
    const matchesDirection = filterDirection === 'all' || log.direction === filterDirection
    return matchesSearch && matchesType && matchesDirection
  })

  const stats = {
    totalCalls: callLogs.filter(l => l.callType === 'call').length,
    totalTexts: callLogs.filter(l => l.callType === 'text').length,
    inbound: callLogs.filter(l => l.direction === 'inbound').length,
    outbound: callLogs.filter(l => l.direction === 'outbound').length,
    linked: callLogs.filter(l => l.linkedTicket).length,
  }

  const getSentimentColor = (sentiment) => {
    switch(sentiment) {
      case 'positive': return 'bg-green-500/20 text-green-200'
      case 'neutral': return 'bg-slate-700/20 text-blue-200'
      case 'negative': return 'bg-red-500/20 text-red-200'
      case 'frustrated': return 'bg-orange-500/20 text-orange-200'
      default: return 'bg-slate-700/20 text-blue-200'
    }
  }

  const getIntentIcon = (intent) => {
    switch(intent) {
      case 'support': return '🆘'
      case 'sales': return '💰'
      case 'complaint': return '⚠️'
      case 'inquiry': return '❓'
      default: return '📞'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Call & Text Logs</h1>
          <p className="text-blue-200 mt-1">Monitor all incoming and outgoing calls and messages from TextNow</p>
        </div>
        <button className="btn-primary flex items-center space-x-2">
          <Download size={20} />
          <span>Export Logs</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Total Calls</p>
            <p className="text-3xl font-bold text-blue-600 mt-2">{stats.totalCalls}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Total Texts</p>
            <p className="text-3xl font-bold text-purple-600 mt-2">{stats.totalTexts}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Inbound</p>
            <p className="text-3xl font-bold text-green-600 mt-2">{stats.inbound}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Outbound</p>
            <p className="text-3xl font-bold text-orange-600 mt-2">{stats.outbound}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-blue-200 text-sm">Linked to Tickets</p>
            <p className="text-3xl font-bold text-cyan-600 mt-2">{stats.linked}</p>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card">
        <div className="card-body space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300" size={20} />
            <input
              type="text"
              placeholder="Search by phone number, ticket ID, or intent..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-base pl-10 w-full"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center space-x-1 ${
                filterType === 'all'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              <Filter size={16} />
              <span>All</span>
            </button>
            <button
              onClick={() => setFilterType('call')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center space-x-1 ${
                filterType === 'call'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              <Phone size={16} />
              <span>Calls</span>
            </button>
            <button
              onClick={() => setFilterType('text')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center space-x-1 ${
                filterType === 'text'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              <MessageCircle size={16} />
              <span>Texts</span>
            </button>
            <button
              onClick={() => setFilterDirection('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filterDirection === 'all'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              All Directions
            </button>
            <button
              onClick={() => setFilterDirection('inbound')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filterDirection === 'inbound'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              Inbound
            </button>
            <button
              onClick={() => setFilterDirection('outbound')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filterDirection === 'outbound'
                  ? 'bg-cyber-600 text-white'
                  : 'bg-slate-700/20 text-blue-200 hover:bg-gray-200'
              }`}
            >
              Outbound
            </button>
          </div>
        </div>
      </div>

      {/* Call Logs List */}
      {filteredLogs.length > 0 ? (
        <div className="space-y-3">
          {filteredLogs.map(log => (
            <div
              key={log.id}
              onClick={() => setSelectedLog(log)}
              className="card hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="card-body">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex items-center space-x-2 text-lg">
                        {log.callType === 'call' ? (
                          <Phone size={24} className="text-blue-600" />
                        ) : (
                          <MessageCircle size={24} className="text-purple-600" />
                        )}
                        <span className="text-sm font-semibold text-white">{log.phoneNumber}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        log.direction === 'inbound' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {log.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                      </span>
                    </div>

                    <p className="text-sm text-blue-200 mb-2">
                      {log.callType === 'call' ? log.transcript : log.message}
                    </p>

                    <div className="flex items-center space-x-4 text-xs text-blue-300/70 flex-wrap gap-2">
                      <span className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span>{log.timestamp}</span>
                      </span>
                      {log.duration && (
                        <span>{log.durationFormatted}</span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full ${getSentimentColor(log.sentiment)}`}>
                        {log.sentiment.charAt(0).toUpperCase() + log.sentiment.slice(1)}
                      </span>
                      <span className="flex items-center space-x-1">
                        <span>{getIntentIcon(log.intent)}</span>
                        <span>{log.intent}</span>
                      </span>
                      {log.linkedTicket && (
                        <span className="flex items-center space-x-1 text-cyan-600 font-medium">
                          <LinkIcon size={14} />
                          <span>{log.linkedTicket}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Phone size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-blue-300/70">No call logs found</p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/50 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-700/30 border-b border-blue-500/30 p-6 flex justify-between items-start">
              <h2 className="text-2xl font-bold text-white">Call Details</h2>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-blue-300/70 hover:text-blue-200"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-blue-200">Phone Number</p>
                  <p className="font-semibold text-white">{selectedLog.phoneNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Type</p>
                  <p className="font-semibold text-white capitalize">{selectedLog.callType}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Direction</p>
                  <p className="font-semibold text-white capitalize">{selectedLog.direction}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Timestamp</p>
                  <p className="font-semibold text-white">{selectedLog.timestamp}</p>
                </div>
                {selectedLog.duration && (
                  <div>
                    <p className="text-sm text-blue-200">Duration</p>
                    <p className="font-semibold text-white">{selectedLog.durationFormatted}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-blue-200">Sentiment</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(selectedLog.sentiment)}`}>
                    {selectedLog.sentiment}
                  </span>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-blue-200 mb-2">AI Summary</p>
                <p className="text-blue-200 bg-slate-700/30 p-3 rounded">{selectedLog.aiSummary}</p>
              </div>

              {selectedLog.transcript && (
                <div className="border-t pt-4">
                  <p className="text-sm text-blue-200 mb-2">Transcript</p>
                  <p className="text-blue-200 bg-slate-700/30 p-3 rounded">{selectedLog.transcript}</p>
                </div>
              )}

              {selectedLog.message && (
                <div className="border-t pt-4">
                  <p className="text-sm text-blue-200 mb-2">Message</p>
                  <p className="text-blue-200 bg-slate-700/30 p-3 rounded">{selectedLog.message}</p>
                </div>
              )}

              {selectedLog.linkedTicket ? (
                <div className="border-t pt-4 bg-cyan-50 p-3 rounded">
                  <p className="text-sm text-blue-200 mb-2">Linked Ticket</p>
                  <button className="text-cyan-600 hover:text-cyan-700 font-semibold">
                    {selectedLog.linkedTicket} →
                  </button>
                </div>
              ) : (
                <div className="border-t pt-4">
                  <button className="btn-primary w-full flex items-center justify-center space-x-2">
                    <LinkIcon size={18} />
                    <span>Link to Ticket</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
